import { useMemo, useState } from 'react'

import { useLazyQuery } from '@apollo/client'
import { Menu, ActionIcon, TextInput } from '@mantine/core'

import { useMutation } from '@redwoodjs/web'

import Tag from 'src/components/DataDisplay/Tag/Tag'
import { toast } from 'src/components/Overlays/Toast/Toast'
import {
  GET_APPLICANT_BY_ID,
  UPDATE_APPLICANT_COMPANY_TAGS,
} from 'src/graphql/applicants.graphql'
import { GET_COMPANY_TAGS } from 'src/graphql/companyTags.graphql'
import IconAdd from 'src/icons/IconAdd'

const onError = () => {
  toast('Unable to update tags, please try again.', 'error')
}

const ApplicantTags = ({ applicant }) => {
  const [loadTags, { data: tagsData, loading }] = useLazyQuery(
    GET_COMPANY_TAGS,
    {
      variables: {
        pageInfo: { limit: 200, offset: 0 },
      },
    }
  )

  const applicantTags = applicant?.tags || []

  const [updateApplicantCompanyTags, { loading: updating }] = useMutation(
    UPDATE_APPLICANT_COMPANY_TAGS,
    {
      refetchQueries: [
        {
          query: GET_APPLICANT_BY_ID,
          variables: {
            id: applicant.applicantId,
          },
        },
      ],
      onCompleted: () => {
        toast('Tags updated successfully', 'success')
      },
      onError: onError,
    }
  )

  const [search, setSearch] = useState('')

  const unusedTags = useMemo(() => {
    const all =
      tagsData?.companyTags?.items?.filter(
        (tag) => !applicantTags.some((t) => t.tagId === tag.tagId)
      ) || []
    const query = search.trim().toLowerCase()
    const filtered = query
      ? all.filter((tag) => tag.label?.toLowerCase().includes(query))
      : all
    return [...filtered].sort((a, b) =>
      (a.label || '').localeCompare(b.label || '', undefined, {
        sensitivity: 'base',
      })
    )
  }, [tagsData, applicantTags, search])

  return (
    <div className="flex w-full flex-1 flex-row items-center gap-2 text-xs text-doubleNickel-gray-500">
      <div className="flex flex-shrink-0 flex-row items-center gap-2 text-xs text-doubleNickel-gray-500">
        Tags:
      </div>
      <div className="flex min-w-0 flex-1 flex-row items-center gap-1 overflow-auto">
        {[...applicantTags].reverse().map((tag) => (
          <Tag
            key={tag.label}
            size="xs"
            showCloseButton={true}
            variant="secondary"
            onClose={() => {
              const newTags = applicantTags
                .filter((t) => t.tagId !== tag.tagId)
                .map((t) => t.tagId)
              updateApplicantCompanyTags({
                variables: {
                  applicantId: applicant.applicantId,
                  tags: newTags,
                },
              })
            }}
          >
            {tag.label}
          </Tag>
        ))}
      </div>
      <Menu
        shadow="md"
        position="bottom-end"
        closeOnItemClick={true}
        onOpen={() => {
          if (!tagsData) {
            loadTags({
              variables: { pageInfo: { offset: 0, limit: 200 } },
            })
          }
        }}
        onClose={() => setSearch('')}
      >
        <Menu.Target>
          <ActionIcon
            variant="subtle"
            color="gray"
            size={24}
            className="ml-auto flex-shrink-0 border border-dashed border-doubleNickel-gray-300"
          >
            <IconAdd className="h-3 w-3 fill-doubleNickel-white stroke-doubleNickel-gray-500" />
          </ActionIcon>
        </Menu.Target>
        <Menu.Dropdown className="flex max-h-[340px] w-[220px] flex-col p-2">
          <TextInput
            size="xs"
            placeholder="Search tags..."
            value={search}
            onChange={(e) => setSearch(e.currentTarget.value)}
            onKeyDown={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
            mb={6}
            autoFocus
          />
          <div className="flex flex-col overflow-y-auto">
            {unusedTags.map((tag) => (
            <Menu.Item
              key={tag.tagId}
              onClick={() => {
                const newTags = [
                  ...applicantTags.map((t) => t.tagId),
                  tag.tagId,
                ]
                updateApplicantCompanyTags({
                  variables: {
                    applicantId: applicant.applicantId,
                    tags: newTags,
                  },
                })
              }}
            >
              {tag.label}
            </Menu.Item>
            ))}
            {unusedTags.length === 0 && !loading && (
              <Menu.Item key="no-tags" disabled>
                No tags found
              </Menu.Item>
            )}
          </div>
        </Menu.Dropdown>
      </Menu>
    </div>
  )
}

export default ApplicantTags
